import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Camera, Search, CheckCircle, XCircle, AlertTriangle, Clock, Users, Gift, RotateCcw, Scan, List, DollarSign, Package, UserCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatEventDateTime, formatEventTimeRange } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function QRScanner({ onScan, scanning }) {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isRunning = useRef(false);

  useEffect(() => {
    if (!scanning) return;
    let mounted = true;

    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!mounted || !scannerRef.current || isRunning.current) return;

        const scanner = new Html5Qrcode('qr-reader');
        html5QrCodeRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 15, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
          (decodedText) => { if (mounted) onScan(decodedText); },
          () => {}
        );
        isRunning.current = true;
      } catch (err) {
        console.error('Scanner error:', err);
        html5QrCodeRef.current = null;
        isRunning.current = false;
        if (mounted) toast.error('Camera unavailable. Use manual lookup.');
      }
    };

    startScanner();

    return () => {
      mounted = false;
      const inst = html5QrCodeRef.current;
      const wasRunning = isRunning.current;
      html5QrCodeRef.current = null;
      isRunning.current = false;

      if (inst && wasRunning) {
        inst.stop().then(() => { try { inst.clear(); } catch {} }).catch(() => { try { inst.clear(); } catch {} });
      } else if (inst) {
        try { inst.clear(); } catch {}
      }
    };
  }, [scanning, onScan]);

  if (!scanning) return null;

  return (
    <div className="relative">
      <div id="qr-reader" ref={scannerRef} className="rounded-lg overflow-hidden" style={{ width: '100%' }} />
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <div className="w-56 h-56 border-2 border-[#D4AF37] rounded-lg" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)' }} />
      </div>
    </div>
  );
}

function TicketResult({ ticket, onCheckIn, onReset, checking, checkInMethod, user, onUndo }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);
  const [undoReason, setUndoReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  if (!ticket) return null;

  const hasAddOns = ticket.purchased_add_ons?.length > 0;
  const isManual = checkInMethod === 'manual';
  const canUndo = ticket.status === 'used' && user && (user.role === 'host' || user.role === 'admin');
  const history = ticket.check_in_history || [];

  const statusConfig = {
    access_granted: { icon: CheckCircle, color: 'text-[#D4AF37]', bg: 'bg-[#1A1A0A] border-[#D4AF37]', label: 'VALID PASS', canCheckIn: true },
    pending_approval: { icon: Clock, color: 'text-[#FFB800]', bg: 'bg-[#1A1A0A] border-[#FFB800]', label: 'PENDING APPROVAL', canCheckIn: false },
    used: { icon: AlertTriangle, color: 'text-[#AFAFAF]', bg: 'bg-[#1A1A1A] border-[#AFAFAF]', label: 'ALREADY CHECKED IN', canCheckIn: false },
    revoked: { icon: XCircle, color: 'text-[#E11D48]', bg: 'bg-[#1A0A0A] border-[#E11D48]', label: 'ACCESS DENIED', canCheckIn: false }
  };

  const cfg = statusConfig[ticket.status] || statusConfig.revoked;
  const Icon = cfg.icon;

  const handleCheckInClick = () => {
    if (isManual) {
      setShowConfirm(true);
    } else {
      onCheckIn(ticket.ticket_id, 'barcode_scan');
    }
  };

  return (
    <div className={`border-2 rounded-lg p-5 ${cfg.bg} transition-all ${hasAddOns && ticket.status === 'used' ? 'vg-pulse-gold' : ''}`} data-testid="scan-result">
      {/* Confirmation Dialog for Manual Check-in */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4" data-testid="manual-checkin-confirm-dialog">
          <div className="bg-[#0A0A0A] border-2 border-[#D4AF37] rounded-lg p-6 max-w-sm w-full">
            <div className="flex items-center space-x-2 mb-4">
              <UserCheck className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-lg font-black text-[#EAE7E1]">Manual Check-In</span>
            </div>
            <div className="space-y-2 mb-5">
              <div className="flex justify-between text-sm"><span className="text-[#777]">Guest</span><span className="text-[#EAE7E1] font-semibold">{ticket.guest_name}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#777]">Confirmation</span><span className="text-[#EAE7E1] font-mono text-xs">{ticket.payment_confirmation}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#777]">Pass</span><span className="text-[#EAE7E1]">{ticket.pass_tier_name || 'General'}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#777]">Attendees</span><span className="text-[#EAE7E1]">{ticket.attendees_count || 1}</span></div>
              {hasAddOns && (
                <div className="flex justify-between text-sm"><span className="text-[#777]">Add-Ons</span><span className="text-[#D4AF37]">{ticket.purchased_add_ons.map(a => a.name).join(', ')}</span></div>
              )}
              <div className="flex justify-between text-sm"><span className="text-[#777]">Status</span><span className="text-[#D4AF37]">{ticket.status === 'access_granted' ? 'Valid' : ticket.status}</span></div>
            </div>
            <p className="text-sm text-[#AFAFAF] mb-4">Are you sure you want to manually check in this guest?</p>
            <div className="flex space-x-3">
              <Button onClick={() => setShowConfirm(false)} variant="outline" className="flex-1 border-[#333] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-lg" data-testid="manual-checkin-cancel">Cancel</Button>
              <Button onClick={() => { setShowConfirm(false); onCheckIn(ticket.ticket_id, 'manual'); }} disabled={checking} className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] text-[#050505] rounded-lg font-bold" data-testid="manual-checkin-confirm">
                {checking ? 'Processing...' : 'Confirm Check-In'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Check-In Confirmation Dialog */}
      {showUndoConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4" data-testid="undo-checkin-confirm-dialog">
          <div className="bg-[#0A0A0A] border-2 border-[#E11D48] rounded-lg p-6 max-w-sm w-full">
            <div className="flex items-center space-x-2 mb-4">
              <RotateCcw className="w-5 h-5 text-[#E11D48]" />
              <span className="text-lg font-black text-[#EAE7E1]">Undo Check-In</span>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm"><span className="text-[#777]">Guest</span><span className="text-[#EAE7E1] font-semibold">{ticket.guest_name}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#777]">Confirmation</span><span className="text-[#EAE7E1] font-mono text-xs">{ticket.payment_confirmation}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#777]">Pass</span><span className="text-[#EAE7E1]">{ticket.pass_tier_name || 'General'}</span></div>
              {hasAddOns && (
                <div className="flex justify-between text-sm"><span className="text-[#777]">Add-Ons</span><span className="text-[#D4AF37]">{ticket.purchased_add_ons.map(a => a.name).join(', ')}</span></div>
              )}
              {ticket.checked_in_by_name && (
                <div className="flex justify-between text-sm"><span className="text-[#777]">Checked in by</span><span className="text-[#EAE7E1]">{ticket.checked_in_by_name} ({ticket.check_in_method === 'manual' ? 'Manual' : ticket.check_in_method === 'barcode_scan' ? 'Scan' : 'Unknown'})</span></div>
              )}
              {ticket.scanned_at && (
                <div className="flex justify-between text-sm"><span className="text-[#777]">At</span><span className="text-[#EAE7E1]">{new Date(ticket.scanned_at).toLocaleString()}</span></div>
              )}
            </div>
            <div className="mb-4">
              <label className="text-xs text-[#777] block mb-1">Reason (optional)</label>
              <input value={undoReason} onChange={(e) => setUndoReason(e.target.value)} placeholder="e.g., Scanned wrong guest" className="w-full px-3 py-2 bg-[#050505] border border-[#333] rounded text-sm text-[#EAE7E1] placeholder-[#555]" data-testid="undo-reason-input" />
            </div>
            <p className="text-sm text-[#AFAFAF] mb-4">This will reopen the ticket for re-entry. The action will be logged.</p>
            <div className="flex space-x-3">
              <Button onClick={() => { setShowUndoConfirm(false); setUndoReason(''); }} variant="outline" className="flex-1 border-[#333] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-lg" data-testid="undo-checkin-cancel">Cancel</Button>
              <Button onClick={() => { setShowUndoConfirm(false); onUndo(ticket.ticket_id, undoReason); setUndoReason(''); }} className="flex-1 bg-[#E11D48] hover:bg-[#E11D48]/80 text-white rounded-lg font-bold" data-testid="undo-checkin-confirm">Undo Check-In</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-center space-x-3 mb-5">
        <Icon className={`w-8 h-8 ${cfg.color}`} />
        <span className={`text-lg font-black tracking-wider ${cfg.color}`} data-testid="scan-status">{cfg.label}</span>
      </div>

      {/* Audit trail for checked-in tickets */}
      {ticket.status === 'used' && (
        <div className="text-center mb-4 space-y-1">
          <div className="text-sm text-[#AFAFAF]">
            Checked in at {new Date(ticket.scanned_at).toLocaleString()}
          </div>
          {ticket.checked_in_by_name && (
            <div className="flex items-center justify-center space-x-2 text-xs" data-testid="audit-trail-info">
              <UserCheck className="w-3.5 h-3.5 text-[#777]" />
              <span className="text-[#AFAFAF]">
                by <span className="text-[#EAE7E1] font-semibold">{ticket.checked_in_by_name}</span>
                <span className="text-[#555]"> ({ticket.checked_in_by_role})</span>
              </span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${ticket.check_in_method === 'manual' ? 'bg-[#FFB800]/20 text-[#FFB800]' : ticket.check_in_method === 'barcode_scan' ? 'bg-green-900/30 text-green-400' : ticket.check_in_method === 'self_checkin' ? 'bg-blue-900/30 text-blue-400' : 'bg-[#555]/20 text-[#777]'}`} data-testid="check-in-method-badge">
                {ticket.check_in_method === 'manual' ? 'MANUAL' : ticket.check_in_method === 'barcode_scan' ? 'SCAN' : ticket.check_in_method === 'self_checkin' ? 'SELF' : 'UNKNOWN'}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        <div className="bg-[#050505] rounded-lg p-4">
          <div className="text-xs text-[#AFAFAF] uppercase tracking-wider mb-1">Guest</div>
          <div className="text-xl font-bold text-[#EAE7E1]" data-testid="scan-guest-name">{ticket.guest_display_name || ticket.guest_name}</div>
          <div className="text-sm text-[#AFAFAF]">{ticket.guest_email}</div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <DetailCard label="Entry Pass" value={ticket.pass_tier_name || 'General'} testId="scan-pass-type" />
          <DetailCard label="Attendees" value={ticket.attendees_count || 1} icon={<Users className="w-4 h-4 text-[#D4AF37]" />} testId="scan-attendees" />
          <DetailCard label="Total Paid" value={ticket.is_comp ? 'COMP' : (ticket.amount_paid != null ? `$${ticket.amount_paid.toFixed(2)}` : '-')} icon={ticket.is_comp ? <Gift className="w-4 h-4 text-green-500" /> : <DollarSign className="w-4 h-4 text-[#D4AF37]" />} testId="scan-amount" />
          <DetailCard label="Payment Method" value={ticket.payment_method_used || '-'} testId="scan-payment-method" />
          <DetailCard label="Confirmation #" value={ticket.payment_confirmation} testId="scan-confirmation" mono />
          <DetailCard label="Access Status" value={
            ticket.status === 'access_granted' ? 'Granted' :
            ticket.status === 'used' ? 'Checked In' :
            ticket.status === 'pending_approval' ? 'Pending' : 'Revoked'
          } testId="scan-access-status" />
        </div>

        {/* ADD-ONS ALERT - Prominent display */}
        {hasAddOns && (
          <div className="bg-gradient-to-r from-[#8C6B1F]/20 via-[#D4AF37]/10 to-[#8C6B1F]/20 border-2 border-[#D4AF37] rounded-lg p-4 vg-addon-glow" data-testid="addon-alert">
            <div className="flex items-center space-x-2 mb-2">
              <Package className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-sm font-black text-[#D4AF37] uppercase tracking-wider">VIP Extras Included</span>
            </div>
            <div className="space-y-1">
              {ticket.purchased_add_ons.map((ao) => (
                <div key={ao.add_on_id || ao.name} className="flex items-center justify-between text-sm" data-testid={`addon-item-${ao.add_on_id || ao.name}`}>
                  <span className="font-semibold text-[#F2D27A]">{ao.name}{ao.quantity > 1 ? ` x${ao.quantity}` : ''}</span>
                  <span className="text-[#D4AF37] font-bold">${(ao.price * (ao.quantity || 1)).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[#050505] rounded-lg p-3">
          <div className="text-xs text-[#AFAFAF] uppercase tracking-wider mb-1">Pass #</div>
          <div className="text-xs font-mono font-semibold text-[#EAE7E1]">{ticket.ticket_number}</div>
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {cfg.canCheckIn && (
          <Button
            onClick={handleCheckInClick}
            disabled={checking}
            className="w-full h-14 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-lg text-lg font-black tracking-wider"
            data-testid="check-in-button"
          >
            {checking ? <div className="w-6 h-6 border-3 border-[#050505] border-t-transparent rounded-full animate-spin" /> : <><CheckCircle className="w-6 h-6 mr-2" />{isManual ? 'MANUAL CHECK IN' : 'CHECK IN'}</>}
          </Button>
        )}
        {canUndo && (
          <Button
            onClick={() => setShowUndoConfirm(true)}
            variant="outline"
            className="w-full border-[#E11D48]/50 text-[#E11D48] hover:bg-[#E11D48]/10 rounded-lg"
            data-testid="undo-check-in-button"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Undo Check-In
          </Button>
        )}
        {history.length > 0 && (
          <button onClick={() => setShowHistory(!showHistory)} className="w-full text-xs text-[#555] hover:text-[#AFAFAF] text-center py-1" data-testid="toggle-history">
            {showHistory ? 'Hide' : 'Show'} History ({history.length})
          </button>
        )}
        {showHistory && history.length > 0 && (
          <div className="bg-[#050505] border border-[#1A1A1A] rounded-lg p-3 space-y-2" data-testid="check-in-history">
            {history.map((h) => (
              <div key={`${h.action}-${h.at}`} className="flex items-start space-x-2 text-xs">
                <span className={`px-1.5 py-0.5 rounded font-bold flex-shrink-0 mt-0.5 ${h.action === 'checked_in' ? 'bg-green-900/30 text-green-400' : 'bg-[#E11D48]/20 text-[#E11D48]'}`}>
                  {h.action === 'checked_in' ? (h.method === 'manual' ? 'MANUAL' : 'SCAN') : 'UNDO'}
                </span>
                <div>
                  <span className="text-[#AFAFAF]">{h.by_name} <span className="text-[#555]">({h.by_role})</span></span>
                  <div className="text-[#555]">{new Date(h.at).toLocaleString()}</div>
                  {h.reason && <div className="text-[#777] italic">"{h.reason}"</div>}
                </div>
              </div>
            ))}
          </div>
        )}
        <Button onClick={onReset} variant="ghost" className="w-full text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A]" data-testid="scan-again-button">
          <RotateCcw className="w-4 h-4 mr-2" /> Scan Next
        </Button>
      </div>
    </div>
  );
}

function DetailCard({ label, value, icon, testId, mono }) {
  return (
    <div className="bg-[#050505] rounded-lg p-3">
      <div className="text-[10px] text-[#AFAFAF] uppercase tracking-wider mb-1">{label}</div>
      <div className="flex items-center space-x-1.5">
        {icon}
        <span className={`text-sm font-semibold text-[#EAE7E1] ${mono ? 'font-mono text-xs' : ''} truncate`} data-testid={testId}>{value}</span>
      </div>
    </div>
  );
}

function AttendeeList({ tickets, onSelectTicket }) {
  const [filter, setFilter] = useState('all');

  const filtered = tickets.filter(t => {
    if (filter === 'checked_in') return t.status === 'used';
    if (filter === 'valid') return t.status === 'access_granted';
    if (filter === 'pending') return t.status === 'pending_approval';
    return t.status !== 'revoked';
  });

  return (
    <div className="space-y-3" data-testid="attendee-list-tab">
      <div className="flex space-x-2 overflow-x-auto pb-1">
        {[['all', 'All'], ['valid', 'Valid'], ['checked_in', 'Checked In'], ['pending', 'Pending']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${filter === key ? 'bg-[#D4AF37] text-[#050505]' : 'bg-[#1A1A1A] text-[#AFAFAF]'}`}
            data-testid={`filter-${key}`}
          >
            {label} ({tickets.filter(t => {
              if (key === 'checked_in') return t.status === 'used';
              if (key === 'valid') return t.status === 'access_granted';
              if (key === 'pending') return t.status === 'pending_approval';
              return t.status !== 'revoked';
            }).length})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-[#AFAFAF]">No guests in this category</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(ticket => (
            <button
              key={ticket.ticket_id}
              onClick={() => onSelectTicket(ticket)}
              className="w-full text-left bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3 hover:border-[#D4AF37]/40 transition-colors"
              data-testid={`attendee-${ticket.ticket_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-[#EAE7E1] text-sm truncate">{ticket.guest_name}</span>
                    {ticket.is_comp && <Gift className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />}
                  </div>
                  <div className="flex items-center space-x-3 mt-1 text-xs text-[#AFAFAF]">
                    <span>{ticket.pass_tier_name || 'General'}</span>
                    <span>{ticket.attendees_count || 1} pax</span>
                    <span>{ticket.payment_method_used || '-'}</span>
                    {ticket.amount_paid != null && !ticket.is_comp && <span>${ticket.amount_paid?.toFixed(2)}</span>}
                    {ticket.purchased_add_ons?.length > 0 && <span className="text-[#D4AF37] font-semibold">+{ticket.purchased_add_ons.length} ADD-ON{ticket.purchased_add_ons.length > 1 ? 'S' : ''}</span>}
                  </div>
                  {ticket.status === 'used' && ticket.checked_in_by_name && (
                    <div className="flex items-center space-x-1.5 mt-1 text-[10px] text-[#555]" data-testid={`audit-${ticket.ticket_id}`}>
                      <UserCheck className="w-3 h-3" />
                      <span>{ticket.checked_in_by_name}</span>
                      <span className={`px-1 py-0.5 rounded text-[8px] font-bold ${ticket.check_in_method === 'manual' ? 'bg-[#FFB800]/20 text-[#FFB800]' : ticket.check_in_method === 'barcode_scan' ? 'bg-green-900/30 text-green-400' : ticket.check_in_method === 'self_checkin' ? 'bg-blue-900/30 text-blue-400' : 'bg-[#555]/20 text-[#777]'}`}>
                        {ticket.check_in_method === 'manual' ? 'MANUAL' : ticket.check_in_method === 'barcode_scan' ? 'SCAN' : ticket.check_in_method === 'self_checkin' ? 'SELF' : 'UNKNOWN'}
                      </span>
                    </div>
                  )}
                </div>
                <span className={`px-2 py-1 text-[10px] font-bold rounded flex-shrink-0 ${
                  ticket.status === 'access_granted' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                  ticket.status === 'used' ? 'bg-green-900/30 text-green-400' :
                  ticket.status === 'pending_approval' ? 'bg-[#FFB800]/20 text-[#FFB800]' :
                  'bg-[#E11D48]/20 text-[#E11D48]'
                }`}>
                  {ticket.status === 'access_granted' ? 'VALID' :
                   ticket.status === 'used' ? 'IN' :
                   ticket.status === 'pending_approval' ? 'PENDING' : 'REVOKED'}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CheckIn() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('scan');
  const [scanning, setScanning] = useState(true);
  const [scannedTicket, setScannedTicket] = useState(null);
  const [checking, setChecking] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [checkedInCount, setCheckedInCount] = useState(0);
  const [user, setUser] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  }, []);

  const goBack = useCallback(() => {
    const stored = localStorage.getItem('user');
    const role = stored ? (JSON.parse(stored).role || 'user') : 'user';
    navigate(role === 'host' || role === 'admin' ? '/dashboard' : '/my-tickets');
  }, [navigate]);

  const refreshData = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [availRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/events/${id}/availability`),
        axios.get(`${API}/tickets?event_id=${id}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAvailability(availRes.data);
      setTickets(ticketsRes.data);
      setCheckedInCount(ticketsRes.data.filter(t => t.status === 'used').length);
    } catch {}
  }, [id]);

  const fetchAll = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [eventRes, availRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/events/${id}/availability`),
        axios.get(`${API}/tickets?event_id=${id}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEvent(eventRes.data);
      setAvailability(availRes.data);
      setTickets(ticketsRes.data);
      setCheckedInCount(ticketsRes.data.filter(t => t.status === 'used').length);
    } catch (error) {
      if (error.response?.status === 403) {
        const reason = error.response?.data?.detail || 'Access denied';
        setAccessDenied(reason);
        try {
          const token = localStorage.getItem('session_token');
          const eventRes = await axios.get(`${API}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          setEvent(eventRes.data);
        } catch {}
      } else {
        toast.error('Failed to load event');
        goBack();
      }
    } finally {
      setLoading(false);
    }
  }, [id, goBack]);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('session_token');
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    try {
      const ws = new WebSocket(`${wsUrl}/api/events/${id}/live?token=${token}`);
      ws.onopen = () => console.log('WS connected');
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ticket_update' || data.type === 'ticket_new') {
            refreshData();
          }
        } catch {}
      };
      ws.onclose = () => {
        setTimeout(() => { if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) connectWebSocket(); }, 3000);
      };
      wsRef.current = ws;
    } catch {}
  }, [id, refreshData]);

  const startPolling = useCallback(() => {
    pollRef.current = setInterval(refreshData, 5000);
  }, [refreshData]);

  useEffect(() => {
    fetchAll();
    fetchUser();
    connectWebSocket();
    startPolling();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchAll, fetchUser, connectWebSocket, startPolling]);

  const handleScan = useCallback(async (decodedText) => {
    if (scannedTicket) return;
    let ticketId = decodedText;
    if (decodedText.startsWith('ticket:')) ticketId = decodedText.replace('ticket:', '');
    setScanning(false);
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}`);
      if (res.data.event_id !== id) { toast.error('Pass is for a different event'); setScanning(true); return; }
      setScannedTicket(res.data);
    } catch { toast.error('Invalid pass'); setScanning(true); }
  }, [scannedTicket, id]);

  const handleCheckIn = async (ticketId, method = 'barcode_scan') => {
    setChecking(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.put(`${API}/tickets/${ticketId}/scan`, { check_in_method: method }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.message === 'Ticket already scanned') {
        toast.info('Already checked in');
        const t = res.data.ticket || {};
        setScannedTicket(prev => ({ ...prev, ...t, status: 'used' }));
      } else {
        toast.success(method === 'manual' ? 'Manually checked in!' : 'Checked in!');
        setCheckedInCount(prev => prev + 1);
        const t = res.data.ticket || {};
        setScannedTicket(prev => ({ ...prev, ...t, status: 'used' }));
      }
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    } finally {
      setChecking(false);
    }
  };

  const handleUndo = async (ticketId, reason = '') => {
    setChecking(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.put(`${API}/tickets/${ticketId}/undo-check-in`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Check-in undone');
      const t = res.data.ticket || {};
      setScannedTicket(prev => ({ ...prev, ...t }));
      setCheckedInCount(prev => Math.max(0, prev - 1));
      refreshData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to undo check-in');
    } finally {
      setChecking(false);
    }
  };

  const handleReset = () => { setScannedTicket(null); if (mode === 'scan') setScanning(true); };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/events/${id}/lookup?q=${encodeURIComponent(searchQuery)}`, { headers: { Authorization: `Bearer ${token}` } });
      setSearchResults(res.data);
      if (res.data.length === 0) toast.info('No matches');
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="access-denied-screen">
        <div className="max-w-sm w-full text-center">
          <div className="w-16 h-16 rounded-full bg-[#1A1A1A] flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-8 h-8 text-[#FFB800]" />
          </div>
          <h2 className="text-xl font-bold text-[#EAE7E1] mb-2">Check-In Access Restricted</h2>
          <p className="text-sm text-[#AFAFAF] mb-6" data-testid="access-denied-reason">{accessDenied}</p>
          {event && (
            <div className="text-xs text-[#555] mb-6">
              <div className="font-medium text-[#777]">{event.title}</div>
              <div>{formatEventTimeRange(event)}</div>
            </div>
          )}
          <Button onClick={goBack} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF]" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const checkedIn = tickets.filter(t => t.status === 'used').length;
  const totalNonRevoked = tickets.filter(t => t.status !== 'revoked');
  const totalAttendees = totalNonRevoked.reduce((s, t) => s + (t.attendees_count || 1), 0);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <div className="bg-[#0A0A0A] border-b border-[#1A1A1A] sticky top-0 z-50">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Button onClick={() => navigate(`/events/${id}/manage`)} variant="ghost" size="sm" className="text-[#AFAFAF] hover:text-[#EAE7E1] -ml-2" data-testid="back-button">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="text-center flex-1 mx-2">
              <div className="text-[#D4AF37] font-black text-xs tracking-[0.2em]">VELVETGATE CHECK-IN</div>
              <div className="text-[#AFAFAF] text-xs truncate">{event?.title}</div>
            </div>
            <div className="text-right min-w-[50px]">
              <div className="text-[#D4AF37] font-bold text-lg leading-none" data-testid="checked-in-counter">{checkedIn}</div>
              <div className="text-[#555] text-[9px] uppercase">Scanned</div>
            </div>
          </div>
        </div>
      </div>

      {/* Capacity Bar */}
      {availability && (
        <div className="bg-[#0A0A0A] border-b border-[#1A1A1A]">
          <div className="max-w-lg mx-auto px-4 py-2">
            {availability.is_unlimited_capacity ? (
              // Unlimited: no cap bar, just counters
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#AFAFAF]">Attendees</span>
                <span className="text-[#EAE7E1] font-medium" data-testid="capacity-info">
                  {totalAttendees} / ∞
                  <span className="text-[#555] ml-2">({checkedIn} in)</span>
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#AFAFAF]">Attendees</span>
                  <span className="text-[#EAE7E1] font-medium" data-testid="capacity-info">
                    {totalAttendees} / {availability.total_capacity}
                    <span className="text-[#555] ml-2">({checkedIn} in)</span>
                  </span>
                </div>
                <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden flex">
                  <div className="h-full bg-green-600 transition-all duration-500" style={{ width: `${Math.min(100, (checkedIn / Math.max(1, availability.total_capacity)) * 100)}%` }} />
                  <div className="h-full bg-[#D4AF37] transition-all duration-500" style={{ width: `${Math.min(100 - (checkedIn / Math.max(1, availability.total_capacity)) * 100, ((totalAttendees - checkedIn) / Math.max(1, availability.total_capacity)) * 100)}%` }} />
                </div>
                <div className="flex justify-between text-[9px] mt-1 text-[#555]">
                  <span className="text-green-500">Checked in: {checkedIn}</span>
                  <span className="text-[#D4AF37]">Remaining: {Math.max(0, availability.total_capacity - totalAttendees)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mode Tabs */}
      <div className="max-w-lg mx-auto px-4 pt-3">
        <div className="flex bg-[#0A0A0A] rounded-lg p-1 border border-[#1A1A1A]">
          {[
            ['scan', Camera, 'Scan'],
            ['manual', Search, 'Lookup'],
            ['list', List, 'List']
          ].map(([key, Icon, label]) => (
            <button
              key={key}
              onClick={() => { setMode(key); setScannedTicket(null); setSearchResults([]); if (key === 'scan') setScanning(true); else setScanning(false); }}
              className={`flex-1 flex items-center justify-center space-x-1.5 py-2 rounded-md text-sm font-semibold transition-all ${mode === key ? 'bg-[#D4AF37] text-[#050505]' : 'text-[#AFAFAF] hover:text-[#EAE7E1]'}`}
              data-testid={`${key}-mode-tab`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 py-3 pb-24">
        {mode === 'scan' && (
          <div className="space-y-3">
            {!scannedTicket && (
              <div className="bg-[#0A0A0A] rounded-lg border border-[#1A1A1A] overflow-hidden" data-testid="scanner-container">
                <QRScanner onScan={handleScan} scanning={scanning} />
                {scanning && (
                  <div className="p-3 text-center">
                    <div className="flex items-center justify-center space-x-2 text-[#D4AF37]">
                      <Scan className="w-4 h-4 animate-pulse" />
                      <span className="text-sm font-medium">Point camera at QR code</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            {scannedTicket && <TicketResult ticket={scannedTicket} onCheckIn={handleCheckIn} onReset={handleReset} checking={checking} checkInMethod="barcode_scan" user={user} onUndo={handleUndo} />}
          </div>
        )}

        {mode === 'manual' && (
          <div className="space-y-3">
            <div className="flex space-x-2" data-testid="manual-search">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Name, email, or confirmation #..."
                className="flex-1 bg-[#0A0A0A] border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] focus:border-[#D4AF37] rounded-lg h-12"
                data-testid="search-input"
              />
              <Button onClick={handleSearch} disabled={searching} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-lg h-12 px-4 font-bold" data-testid="search-button">
                {searching ? <div className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full animate-spin" /> : <Search className="w-5 h-5" />}
              </Button>
            </div>

            {searchResults.length > 0 && !scannedTicket && (
              <div className="space-y-2" data-testid="search-results">
                <div className="text-xs text-[#AFAFAF] uppercase tracking-wider px-1">{searchResults.length} result(s)</div>
                {searchResults.map(ticket => (
                  <button
                    key={ticket.ticket_id}
                    onClick={() => { setScannedTicket(ticket); setSearchResults([]); }}
                    className="w-full text-left bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3 hover:border-[#D4AF37]/50 transition-colors"
                    data-testid={`search-result-${ticket.ticket_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-[#EAE7E1] text-sm">{ticket.guest_name}</div>
                        <div className="text-xs text-[#AFAFAF]">{ticket.pass_tier_name || 'General'} | {ticket.attendees_count || 1} pax | {ticket.payment_confirmation}</div>
                        {ticket.purchased_add_ons?.length > 0 && (
                          <div className="text-[10px] text-[#D4AF37] font-semibold mt-0.5">+ {ticket.purchased_add_ons.length} ADD-ON{ticket.purchased_add_ons.length > 1 ? 'S' : ''}</div>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-[10px] font-bold rounded ${
                        ticket.status === 'access_granted' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                        ticket.status === 'used' ? 'bg-green-900/30 text-green-400' :
                        ticket.status === 'pending_approval' ? 'bg-[#FFB800]/20 text-[#FFB800]' :
                        'bg-[#E11D48]/20 text-[#E11D48]'
                      }`}>
                        {ticket.status === 'access_granted' ? 'VALID' : ticket.status === 'used' ? 'IN' : ticket.status === 'pending_approval' ? 'PENDING' : 'REVOKED'}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {scannedTicket && <TicketResult ticket={scannedTicket} onCheckIn={handleCheckIn} onReset={() => { setScannedTicket(null); setSearchResults([]); }} checking={checking} checkInMethod="manual" user={user} onUndo={handleUndo} />}
          </div>
        )}

        {mode === 'list' && (
          <AttendeeList tickets={tickets} onSelectTicket={(ticket) => { setScannedTicket(ticket); setMode('manual'); }} />
        )}
      </div>
    </div>
  );
}
